import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Download, Share2, Images, ArrowRight, Award, Lock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { readBadges, makeBadgeDataUrl, shareBadge, STREAK_TIERS, readStreakRewards } from "@/lib/trainerStore";
import { PrizeBadges, usePrizeBadges } from "@/components/PrizeBadges";
import { useAuth } from "@/context/AuthContext";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch (e) {
    return "";
  }
}

const METRIC_LABEL = { streak: "Best streak", acc: "Accuracy", notes: "Most notes" };

export default function Badges() {
  const { user } = useAuth();
  const prize = usePrizeBadges(!!user);
  const [badges] = useState(() => readBadges());
  const [streakRewards] = useState(() => readStreakRewards());

  const download = (b) => {
    const url = makeBadgeDataUrl({ difficulty: b.difficulty, streak: b.streak, accuracy: b.accuracy, notes: b.notes });
    const a = document.createElement("a");
    a.href = url;
    a.download = `jt-personal-best-${b.difficulty}.png`;
    a.click();
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 sm:px-8 py-10 lg:py-16">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Your achievements</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Badge Gallery</h1>
        <p className="mt-4 max-w-xl text-muted-foreground">
          Every personal best you set in the Sight-Reading Trainer is kept here on this device. Re-download or share any badge.
        </p>

        {user && <PrizeBadges data={prize} />}

        {/* Streak rewards from the Daily Challenge */}
        <div className="mt-10" data-testid="badges-streak-rewards">
          <div className="flex items-center gap-2">
            <Award size={18} className="text-primary" />
            <h2 className="font-serif text-2xl">Daily Challenge streak rewards</h2>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {STREAK_TIERS.map((t) => {
              const unlocked = streakRewards.some((r) => r.days === t.days);
              return (
                <div
                  key={t.days}
                  data-testid={`reward-tier-${t.days}`}
                  className={`rounded-2xl border p-4 text-center ${unlocked ? "border-primary/40 bg-card" : "border-dashed border-border bg-card/50"}`}
                >
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={unlocked ? { backgroundColor: t.color + "26" } : undefined}>
                    {unlocked ? <Award size={22} style={{ color: t.color }} /> : <Lock size={18} className="text-muted-foreground/50" />}
                  </span>
                  <p className={`mt-2 font-serif text-base leading-tight ${unlocked ? "text-foreground" : "text-muted-foreground/60"}`}>{t.label}</p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.blurb}</p>
                </div>
              );
            })}
          </div>
        </div>

        {badges.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="badges-empty">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Images size={26} /></span>
            <h3 className="mt-5 font-serif text-2xl">No badges yet</h3>
            <p className="mt-2 text-sm text-muted-foreground">Set a personal best in the trainer to earn your first badge.</p>
            <Link to="/trainer">
              <Button data-testid="badges-go-trainer" className="mt-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                Open the trainer <ArrowRight size={15} className="ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="badges-grid">
            {badges.map((b) => {
              const recs = Object.keys(b.records || {}).filter((k) => b.records[k]);
              return (
                <div key={b.id} data-testid={`badge-card-${b.id}`} className="overflow-hidden rounded-2xl border border-border bg-card">
                  <div className="relative bg-neutral-900 p-6 text-center" style={{ background: "#121212" }}>
                    <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary"><Trophy size={22} /></span>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-primary">{b.difficulty} level</p>
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      <div>
                        <p className="font-serif text-2xl text-[#f2ede3]">{b.streak}</p>
                        <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-400">Streak</p>
                      </div>
                      <div>
                        <p className="font-serif text-2xl text-[#f2ede3]">{b.accuracy}%</p>
                        <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-400">Accuracy</p>
                      </div>
                      <div>
                        <p className="font-serif text-2xl text-[#f2ede3]">{b.notes}</p>
                        <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-400">Notes</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex flex-wrap gap-1.5">
                      {recs.map((r) => (
                        <span key={r} className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary">{METRIC_LABEL[r] || r}</span>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">{fmtDate(b.date)}</p>
                    <div className="mt-3 flex gap-2">
                      <Button variant="outline" onClick={() => download(b)} data-testid={`badge-download-${b.id}`} className="flex-1 rounded-full border-border text-sm">
                        <Download size={14} className="mr-1.5" /> Save
                      </Button>
                      <Button onClick={() => shareBadge({ difficulty: b.difficulty, streak: b.streak, accuracy: b.accuracy, notes: b.notes })} data-testid={`badge-share-${b.id}`} className="flex-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm">
                        <Share2 size={14} className="mr-1.5" /> Share
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
